// Stats + learner types. Mirror the FastAPI schemas:
//   - global: app/api/v1/stats.py (GlobalStatsResponse, FrameworkStat, TopicStat)
//   - tree:   app/api/v1/tree.py  (TreeResponse, TreeNode, UserLevel)
//   - learner: app/schemas/telemetry.py (Progression, StatsSnapshot, ...)

// ----- Global ("across all players") -----

export interface FrameworkStat {
  framework_slug: string;
  questions_answered: number;
  sets_generated: number;
  unique_users: number;
}

export interface GlobalTopicStat {
  topic_slug: string;
  questions_answered: number;
  accuracy_percent: number;
  unique_users: number | null;
  mean_ability: number | null;
  mean_difficulty: number | null;
}

export interface GlobalStats {
  total_questions_answered: number;
  total_correct: number;
  overall_accuracy_percent: number;
  total_sets_generated: number;
  total_active_users: number;
  by_framework: FrameworkStat[];
  by_topic: GlobalTopicStat[];
}

// ----- Knowledge tree (public + per-user annotated) -----

export interface UserLevel {
  attempts: number;
  correct: number;
  percentile: number | null;
  mastery: number | null;
}

export interface TreeNode {
  node_id: string;
  node_slug: string;
  kind: "subject" | "area" | "topic" | string;
  title: string;
  user_count: number;
  attempt_count: number;
  accuracy: number | null; // 0..1
  mean_theta_percentile: number | null; // 0..100
  mean_mastery: number | null; // 0..1
  user_level: UserLevel | null;
  children: TreeNode[];
}

export interface TreeResponse {
  region: string | null;
  nodes: TreeNode[];
}

// ----- Learner (per-user) -----

export interface Progression {
  total_xp: number;
  tier: string;
  tier_rung_index: number;
  current_streak: number;
  longest_streak: number;
  last_active_date: string | null;
  calibrated_at: string | null;
}

export interface LearnerTopicStat {
  topic_slug: string;
  theta: number | null;
  percentile: number | null;
  mastery: number | null;
  recent_accuracy: number | null;
  n_attempts: number;
  flag: "weak" | "strong" | "neutral" | string;
}

export interface StatsSnapshot {
  user_id: string;
  topics: LearnerTopicStat[];
  weak_topics: string[];
  generated_at: string;
}

export interface MistakeTopic {
  topic_slug: string;
  miss_count: number;
  attempt_count: number;
  miss_rate: number | null;
  last_missed_at: string | null;
  wrong_question_ids: string[];
}

export interface MistakeFocus {
  user_id: string;
  topics: MistakeTopic[];
  generated_at: string;
}

export interface TrendPoint {
  date: string;
  theta_percentile: number | null;
  mastery: number | null;
  recent_accuracy: number | null;
}

export interface TopicTrend {
  topic_slug: string;
  points: TrendPoint[];
}

export interface AbilityTrend {
  topics: TopicTrend[];
  generated_at: string;
}

export type ProgressPeriod = "total" | "weekly" | "monthly";

export interface ProgressWindow {
  period: string;
  answered: number;
  correct: number;
  accuracy_percent: number;
  xp_earned: number;
  minutes_earned: number;
}

// ----- Strengths & gaps breakdown (mirror telemetry.py StatsBreakdown) -----

export type BreakdownWindow = "7" | "30" | "all";
export type BreakdownDimension = "by_topic" | "by_set" | "by_framework";

export interface BreakdownRow {
  key: string;
  label: string;
  attempts: number;
  accuracy: number; // 0..1
  last_at: string | null;
  framework_slug: string | null;
  folder_id: string | null;
  archived: boolean;
  set_count: number | null;
}

export interface StatsBreakdown {
  window: string;
  by_topic: BreakdownRow[];
  by_set: BreakdownRow[];
  by_framework: BreakdownRow[];
}

// ----- Regions (mirror app/services/regions.py REGIONS) -----

export const REGIONS = [
  "North America",
  "Latin America",
  "Europe",
  "Middle East & Africa",
  "South Asia",
  "East Asia & Pacific",
  "Oceania",
] as const;

export type Region = (typeof REGIONS)[number];
